import { useEffect, useRef } from 'react';

interface UseDialogViewTrackingOptions {
    /**
     * Whether the dialog is currently open
     */
    isOpen: boolean;

    /**
     * Whether the dialog should be unmounted (e.g., for iOS Safari layout bugs)
     * When true, tracking state won't be updated
     */
    shouldUnmount?: boolean;
}

/**
 * Manages dialog view tracking state to prevent duplicate analytics events
 * when dialogs are remounted.
 *
 * @returns shouldSkipTracking - true if analytics tracking should be skipped
 *
 * @example
 * ```tsx
 * const shouldSkipTracking = useDialogViewTracking({
 *     isOpen: isDialogOpen,
 *     shouldUnmount: isIOS() && isDetailsOpen
 * });
 *
 * <Dialog
 *     isOpen={isDialogOpen}
 *     skipDialogViewedTracking={shouldSkipTracking}
 * />
 * ```
 */
export function useDialogViewTracking({
    isOpen,
    shouldUnmount = false,
}: UseDialogViewTrackingOptions): boolean {
    const hasTrackedRef = useRef(false);

    // Reset tracking flag when dialog closes
    useEffect(() => {
        if (!isOpen) {
            hasTrackedRef.current = false;
        }
    }, [isOpen]);

    // Set tracking flag when dialog opens (and isn't unmounted)
    useEffect(() => {
        if (isOpen && !shouldUnmount && !hasTrackedRef.current) {
            hasTrackedRef.current = true;
        }
    }, [isOpen, shouldUnmount]);

    // Return whether tracking should be skipped
    return hasTrackedRef.current;
}
